import type { ProductGroupResource } from "../../product-group";
import type { CustomerGroupResource } from "../../customer-group";
import type { CountryResource } from "../../country";
import type { CustomerResource } from "../../customer";
import type { NoteResource } from "../../note";
import type { VariantResource } from "../../variant";
import type { MarketplaceResource } from "../../marketplace";
import type { DiscountItemResource } from "../../discount-item";
import type { ProductResource } from "../../product";
import type { Nullable } from "../../../common.ts";

export enum DiscountTypeEnum {
  Percent = "%",
  Absolute = "abs",
  Item = "itm",
}

export enum DiscountStatus {
  Active = "active",
  Inactive = "inactive",
  ActiveDuringPeriod = "active_during_period",
}

export type DiscountStoreRequest = {
  title: string;
  is_active?: Nullable<boolean>;
  active_from?: Nullable<string>;
  active_to?: Nullable<string>;
  price_from?: Nullable<string>;
  price_to?: Nullable<string>;
  amount_from?: Nullable<number>;
  amount_to?: Nullable<number>;
  discount_type: DiscountTypeEnum;
  is_price_net?: Nullable<boolean>;
  is_price_discount?: Nullable<boolean>;
  sort?: Nullable<number>;
  countries?: Record<
    string,
    {
      is_excluded: boolean;
    }
  >;
  customers?: Record<
    string,
    {
      is_excluded: boolean;
    }
  >;
  customerGroups?: Record<
    string,
    {
      is_excluded: boolean;
    }
  >;
  productGroups?: Record<
    string,
    {
      is_excluded: boolean;
    }
  >;
  products?: Record<
    string,
    {
      is_excluded: boolean;
    }
  >;
  marketplaces?: Record<
    string,
    {
      is_excluded: boolean;
    }
  >;
  discount_items?: {
    variant_id: string;
    is_multiple: boolean;
    quantity: number;
  }[];
};

export type DiscountUpdateRequest = {
  title?: string | null;
  is_active?: boolean;
  price_from?: string;
  price_to?: string;
  amount_from?: number;
  amount_to?: number;
  is_price_net?: boolean;
  is_price_discount?: boolean;
  discount_type?: DiscountTypeEnum;
  sort?: number;
  countries?: Record<
    string,
    {
      is_excluded: boolean;
    }
  >;
  customers?: Record<
    string,
    {
      is_excluded: boolean;
    }
  >;
  customerGroups?: Record<
    string,
    {
      is_excluded: boolean;
    }
  >;
  products?: Record<
    string,
    {
      is_excluded: boolean;
    }
  >;
  marketplaces?: Record<string, object>;
  discount_items?: {
    variant_id: string;
    is_multiple: boolean;
    quantity: number;
  }[];
};

export interface DiscountResource {
  id: string;
  gid: string;
  title: string;
  is_active: boolean;
  sort: number;
  is_price_net: boolean;
  is_discount_net: boolean;
  active_from: string | null;
  active_to: string | null;
  price_from: string;
  price_to: string;
  amount_from: number;
  amount_to: number;
  discount_type: DiscountTypeEnum;
  discount_amount: string | null;

  discount_items?: DiscountItemResource[];
  discount_items_count?: number;

  marketplaces?: MarketplaceResource[];

  excluded_variants?: VariantResource[];
  excluded_variants_count: number;
  included_variants?: VariantResource[];
  included_variants_count: number;

  excluded_products?: ProductResource[];
  excluded_products_count: number;
  included_products?: ProductResource[];
  included_products_count: number;

  excluded_product_groups?: ProductGroupResource[];
  excluded_product_groups_count: number;
  included_product_groups?: ProductGroupResource[];
  included_product_groups_count: number;

  excluded_customer_groups?: CustomerGroupResource[];
  excluded_customer_groups_count: number;
  included_customer_groups?: CustomerGroupResource[];
  included_customer_groups_count: number;

  excluded_countries?: CountryResource[];
  excluded_countries_count: number;
  included_countries?: CountryResource[];
  included_countries_count: number;

  excluded_customers?: CustomerResource[];
  excluded_customers_count: number;
  included_customers?: CustomerResource[];
  included_customers_count: number;

  notes?: NoteResource[];
  notes_count: number;
}
